import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { message } from "antd";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/user";
import CondimentUI from "./components/CondimentUI";
import { useLocation, useParams } from "react-router-dom";
import {
  getAdminCondiments,
  getCondimentItems,
  updateCondimentOrder,
  updateCondimentStatus,
} from "../../api/Condiments";

const Condiments = () => {
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [summaryData, setSummaryData] = useState({
    condiments: [],
    setmenus: [],
  });
  const [activeType, setActiveType] = useState("condiments");
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [updatingStatusMap, setUpdatingStatusMap] = useState({});
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const { pathname } = useLocation();
  const params = useParams();
  const user = useSelector(selectUser);
  const latestItemsRequest = useRef(0);
  const branchId = params.branchId || user.branch;

  const normalizeSummaryGroup = useCallback((groups = []) => {
    return groups.map((group) => ({
      id: group.id,
      name: group.name || group.nombre || "-",
      totalProducts: Number(group.totalp ?? group.totalProducts ?? 0),
    }));
  }, []);

  const normalizeItem = useCallback((item) => {
    return {
      ...item,
      id: item.id,
      name: item.name || item.nombre || "-",
      price: Number(item.price ?? 0),
      setOrder: Number(item.setOrder ?? 0),
    };
  }, []);

  const loadItems = useCallback(async (groupId, type) => {
    if (!groupId || !type) {
      setItems([]);
      return;
    }

    const requestId = latestItemsRequest.current + 1;
    latestItemsRequest.current = requestId;
    setLoadingItems(true);

    try {
      const response = await getCondimentItems({ id: groupId, type });

      if (requestId !== latestItemsRequest.current) {
        return;
      }

      if (response.error || response.status !== 200) {
        throw new Error("request_failed");
      }

      const nextItems = Array.isArray(response.data?.items)
        ? response.data.items.map(normalizeItem)
        : [];

      setItems(nextItems);
    } catch (error) {
      if (requestId === latestItemsRequest.current) {
        setItems([]);
      }
      message.error("¡Hubo un problema!");
    } finally {
      if (requestId === latestItemsRequest.current) {
        setLoadingItems(false);
      }
    }
  }, [normalizeItem]);

  const initialFetch = useCallback(async () => {
    setLoadingSummary(true);

    try {
      const response = await getAdminCondiments(branchId);

      if (response.error || response.status !== 200) {
        throw new Error("request_failed");
      }

      const normalizedSummary = {
        condiments: normalizeSummaryGroup(response.data?.condiments),
        setmenus: normalizeSummaryGroup(
          response.data?.setmenus || response.data?.setmenu
        ),
      };

      setSummaryData(normalizedSummary);

      const defaultType =
        normalizedSummary.condiments.length > 0
          ? "condiments"
          : normalizedSummary.setmenus.length > 0
            ? "setmenus"
            : "condiments";

      const defaultGroup = normalizedSummary[defaultType]?.[0] || null;

      setActiveType(defaultType);
      setSelectedGroupId(defaultGroup?.id ?? null);
      setSearch("");

      if (defaultGroup?.id) {
        await loadItems(defaultGroup.id, defaultType);
      } else {
        setItems([]);
      }
    } catch (error) {
      setSummaryData({ condiments: [], setmenus: [] });
      setSelectedGroupId(null);
      setItems([]);
      message.error("¡Hubo un problema!");
    } finally {
      setLoadingSummary(false);
    }
  }, [branchId, loadItems, normalizeSummaryGroup]);

  useEffect(() => {
    initialFetch();
  }, [initialFetch]);

  const summaryList = useMemo(() => {
    return summaryData[activeType] || [];
  }, [activeType, summaryData]);

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return items;
    }

    return items.filter((item) => {
      const name = `${item.name || ""}`.toLowerCase();
      const id = `${item.id || ""}`.toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }, [items, search]);

  const selectedGroup = useMemo(() => {
    return summaryList.find((group) => group.id === selectedGroupId) || null;
  }, [selectedGroupId, summaryList]);

  const handleTypeChange = useCallback(async (nextType) => {
    if (nextType === activeType) {
      return;
    }

    const nextGroups = summaryData[nextType] || [];
    const nextSelected = nextGroups[0] || null;

    setActiveType(nextType);
    setSelectedGroupId(nextSelected?.id ?? null);
    setSearch("");

    if (nextSelected?.id) {
      await loadItems(nextSelected.id, nextType);
    } else {
      setItems([]);
    }
  }, [activeType, loadItems, summaryData]);

  const handleGroupSelect = useCallback(async (group) => {
    if (!group?.id || group.id === selectedGroupId) {
      return;
    }

    setSelectedGroupId(group.id);
    setSearch("");
    await loadItems(group.id, activeType);
  }, [activeType, loadItems, selectedGroupId]);

  const handleStatusChange = useCallback(async (itemId, statusMode, checked) => {
    const requestKey = `${itemId}-${statusMode}`;
    const statusKey = `status_${statusMode}`;
    const nextValue = checked ? 1 : 0;

    setUpdatingStatusMap((current) => ({
      ...current,
      [requestKey]: true,
    }));

    setItems((current) =>
      current.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [statusKey]: nextValue,
            }
          : item
      )
    );

    try {
      const response = await updateCondimentStatus({
        id: itemId,
        status_mode: statusMode,
        status_value: nextValue,
        type: activeType,
        user: user.id,
      });

      if (response.error || response.status !== 200) {
        throw new Error("request_failed");
      }
    } catch (error) {
      setItems((current) =>
        current.map((item) =>
          item.id === itemId
            ? {
                ...item,
                [statusKey]: checked ? 0 : 1,
              }
            : item
        )
      );
      message.error("¡Hubo un problema!");
    } finally {
      setUpdatingStatusMap((current) => {
        const next = { ...current };
        delete next[requestKey];
        return next;
      });
    }
  }, [activeType, user.id]);

  const handleReorder = useCallback((draggedId, targetId) => {
    if (!draggedId || !targetId || draggedId === targetId) {
      setDragOverId(null);
      return;
    }

    if (!selectedGroupId || savingOrder) {
      setDragOverId(null);
      return;
    }

    const sourceIndex = items.findIndex(
      (item) => String(item.id) === String(draggedId)
    );
    const targetIndex = items.findIndex(
      (item) => String(item.id) === String(targetId)
    );

    if (sourceIndex < 0 || targetIndex < 0) {
      setDragOverId(null);
      return;
    }

    const previousItems = items;
    const nextItems = [...items];
    const [movedItem] = nextItems.splice(sourceIndex, 1);
    nextItems.splice(targetIndex, 0, movedItem);

    const normalizedNextItems = nextItems.map((item, index) => ({
      ...item,
      setOrder: index + 1,
    }));

    setItems(normalizedNextItems);
    setDragOverId(null);
    setSavingOrder(true);

    updateCondimentOrder({
      parent_id: selectedGroupId,
      type: activeType,
      user: user.id,
      items: normalizedNextItems.map((item) => ({
        id: item.id,
        setOrder: item.setOrder,
      })),
    })
      .then((response) => {
        if (
          response.error ||
          response.status !== 200 ||
          response.data?.success === false ||
          response.data?.success === "false"
        ) {
          throw new Error("request_failed");
        }
      })
      .catch(() => {
        setItems(previousItems);
        message.error("No se pudo guardar el nuevo orden.");
      })
      .finally(() => {
        setSavingOrder(false);
      });
  }, [activeType, items, savingOrder, selectedGroupId, user.id]);

  const handleActionClick = useCallback((label) => {
    message.info(`${label} estará disponible en una siguiente fase.`);
  }, []);

  const refreshSelectedGroup = useCallback(async () => {
    if (!selectedGroupId) {
      return;
    }

    await loadItems(selectedGroupId, activeType);
  }, [activeType, loadItems, selectedGroupId]);

  return (
    <CondimentUI
      activeType={activeType}
      dragOverId={dragOverId}
      filteredItems={filteredItems}
      items={items}
      loadingItems={loadingItems}
      loadingSummary={loadingSummary}
      pathname={pathname}
      savingOrder={savingOrder}
      search={search}
      selectedGroup={selectedGroup}
      summaryList={summaryList}
      updatingStatusMap={updatingStatusMap}
      onActionClick={handleActionClick}
      onDragOverChange={setDragOverId}
      onGroupSelect={handleGroupSelect}
      onReorder={handleReorder}
      onRetry={refreshSelectedGroup}
      onSearchChange={setSearch}
      onStatusChange={handleStatusChange}
      onTypeChange={handleTypeChange}
    />
  );
};

export default Condiments;
